// Global keyboard shortcuts. Browser-reserved combos (Ctrl+T/W/L/…) are handled
// best-effort with preventDefault; everything is also reachable via the command
// palette and toolbar, so a reserved key being swallowed by the host browser is
// never a dead end.
import { state, active } from "./state.js";
import { newTab, closeTab, openSettings, selectTab } from "./tabs.js";
import { back, forward, reload } from "./navigation.js";
import { reopenClosed, duplicateActive, newTabAction } from "./session.js";
import { zoomActive, openFind } from "./zoom.js";
import { togglePalette } from "./palette.js";
import { toggleFavorite } from "./favorites-ui.js";
import { toggleCollapse } from "./collapse.js";
import { addrInput } from "./dom.js";
import { get } from "../shared/settings-store.js";
import { islandKey, islandAddr, islandOn } from "./island-gate.js";
import { readGate, isReadKey } from "./read-gate.js";
import { helperKey } from "./helper-dock.js";

function typing(e) {
	const el = e.target;
	return el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);
}
function focusAddr() { if (islandAddr()) return; if (addrInput) { addrInput.focus(); addrInput.select(); } }
function cycle(dir) {
	const i = state.tabs.findIndex((t) => t.id === state.activeId);
	if (i < 0 || state.tabs.length < 2) return;
	const n = (i + dir + state.tabs.length) % state.tabs.length;
	selectTab(state.tabs[n].id);
}

const isAddrKey = (e) => e.key === "F6" || (e.altKey && !e.ctrlKey && !e.metaKey && /^[dDlL]$/.test(e.key));

// Pages in tabs (same-origin frames) get only the keys that move focus back to the address bar,
// so a page that holds on to Tab never traps the keyboard and its own shortcuts keep working.
const watched = new WeakSet();
export function watchFrameKeys(win) {
	try {
		// Keyed on the document, not the window: a frame's first about:blank window can be reused by
		// the page that loads into it, and that page still needs its own listener.
		const doc = win && win.document;
		if (!doc || watched.has(doc)) return;
		watched.add(doc);
		win.addEventListener("keydown", (e) => {
			// In Island Mode the island's Alt keys (Alt+I, Alt+. and Alt+, , Alt+T, Alt+W) also work from inside a page.
			if (isReadKey(e)) { e.preventDefault(); e.stopPropagation(); readGate.toggle(); return; }
			if (isAddrKey(e) || (e.altKey && !e.ctrlKey && !e.metaKey && islandOn() && /^(KeyI|Period|Comma|KeyT|KeyW)$/.test(e.code) && islandKey(e))) { e.preventDefault(); e.stopPropagation(); if (isAddrKey(e)) focusAddr(); }
			else if (helperKey(e)) e.stopPropagation();
		}, true);
	} catch (e) { /* cross-origin frame */ }
}

export function initShortcuts() {
	window.addEventListener("keydown", onKey, true);
}

function onKey(e) {
	if (islandKey(e)) { e.preventDefault(); return; }
	if (isReadKey(e)) { e.preventDefault(); readGate.toggle(); return; }
	const mod = e.ctrlKey || e.metaKey;
	const k = e.key;

	if (mod && (k === "k" || k === "K")) { e.preventDefault(); if (get("paletteEnabled") !== false && !islandOn()) togglePalette(); return; } // Island Mode has its own
	if (mod && (k === "f" || k === "F") && !e.shiftKey) { e.preventDefault(); openFind(); return; }
	if (mod && k === ",") { e.preventDefault(); openSettings(); return; }
	if (mod && (k === "l" || k === "L")) { e.preventDefault(); focusAddr(); return; }
	// Chrome keeps Ctrl+L, Alt+D and F6 for its own address bar; Alt+L is ours.
	if (isAddrKey(e)) { e.preventDefault(); focusAddr(); return; }
	if (helperKey(e)) return;
	if (mod && (k === "t" || k === "T")) { e.preventDefault(); e.shiftKey ? reopenClosed() : newTabAction(); return; }
	if (mod && (k === "w" || k === "W")) { e.preventDefault(); const a = active(); if (a && !a.pinned) closeTab(a.id); return; }
	if (mod && e.shiftKey && (k === "d" || k === "D")) { e.preventDefault(); duplicateActive(); return; }
	if (mod && e.shiftKey && (k === "h" || k === "H")) { e.preventDefault(); toggleCollapse(); return; }
	if (mod && !e.shiftKey && (k === "d" || k === "D")) { e.preventDefault(); toggleFavorite(); return; }
	if (mod && (k === "r" || k === "R") && !e.shiftKey) { e.preventDefault(); reload(); return; }
	if (mod && (k === "=" || k === "+")) { e.preventDefault(); zoomActive(1); return; }
	if (mod && k === "-") { e.preventDefault(); zoomActive(-1); return; }
	if (mod && k === "0") { e.preventDefault(); zoomActive(0); return; }
	if (e.ctrlKey && k === "Tab") { e.preventDefault(); cycle(e.shiftKey ? -1 : 1); return; }
	if (mod && /^[1-9]$/.test(k)) { e.preventDefault(); const t = state.tabs[+k - 1]; if (t) selectTab(t.id); return; }
	if (e.altKey && k === "ArrowLeft") { e.preventDefault(); back(); return; }
	if (e.altKey && k === "ArrowRight") { e.preventDefault(); forward(); return; }
	if (!mod && !e.altKey && k === "/" && !typing(e) && get("slashShortcut") !== false) { e.preventDefault(); focusAddr(); return; }
}
